import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Upload, Image, X, Loader2, Star } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import { getProduct, createProduct, updateProduct, uploadImage, getCategories } from '../api/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProductEditPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { language } = useStore();
  const t = translations[language];
  const isNew = productId === 'new';
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(!isNew);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    category: 'sweaters',
    price: '',
    original_price: '',
    featured: false,
    new_arrival: false,
    images: [''],
    color_images: {}, // { "Noir": ["img1.jpg"], "Rouge": ["img2.jpg"] }
    translations: {
      fr: { name: '', description: '', material: '', care_instructions: '' },
      nl: { name: '', description: '', material: '', care_instructions: '' },
    },
    variants: [{ size: 'M', color: 'Noir', color_hex: '#000000', stock: 10 }],
  });
  const [selectedColorForImages, setSelectedColorForImages] = useState(null);
  const [categories, setCategories] = useState([]);

  // Load categories from API
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        // Handle both new format (from db.categories) and legacy format
        if (data && Array.isArray(data)) {
          const cats = data.map(cat => {
            if (cat.slug) {
              // New format from db.categories
              const name = cat.translations?.[language]?.name || cat.translations?.fr?.name || cat.slug;
              return { value: cat.slug, label: name };
            } else if (cat.category) {
              // Legacy format from products aggregation
              return { value: cat.category, label: cat.category };
            }
            return null;
          }).filter(Boolean);
          setCategories(cats);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        // Fallback to default categories
        setCategories([
          { value: 'sweaters', label: t.nav?.sweaters || 'Pulls' },
          { value: 'hoodies', label: t.nav?.hoodies || 'Hoodies' },
          { value: 'tshirts', label: t.nav?.tshirts || 'T-shirts' },
          { value: 'jackets', label: t.nav?.jackets || 'Vestes' },
        ]);
      }
    };
    loadCategories();
  }, [language]);

  useEffect(() => {
    if (!isNew) {
      loadProduct();
    }
  }, [productId]);

  const loadProduct = async () => {
    try {
      const data = await getProduct(productId);
      
      // Clean up broken images - check which images actually exist
      let validImages = [];
      let validColorImages = {};
      
      if (data.images?.length) {
        // Check each image URL
        for (const img of data.images) {
          if (!img || img.trim() === '') continue;
          
          // Cloudinary images - assume they're valid (they have proper URLs)
          if (img.includes('res.cloudinary.com')) {
            validImages.push(img);
            continue;
          }
          
          // External URLs (pexels, unsplash, etc.) - assume valid
          if (img.startsWith('http') && !img.includes('/api/uploads/')) {
            validImages.push(img);
            continue;
          }
          
          // Local uploads - check if they exist
          if (img.includes('/api/uploads/') || img.startsWith('/api/uploads/')) {
            try {
              const checkUrl = img.startsWith('http') ? img : `${BACKEND_URL}${img}`;
              const response = await fetch(checkUrl, { method: 'HEAD' });
              if (response.ok) {
                validImages.push(img);
              } else {
                console.log(`Removing broken image: ${img}`);
              }
            } catch (e) {
              console.log(`Removing unreachable image: ${img}`);
            }
          } else {
            // Other URLs - keep them
            validImages.push(img);
          }
        }
      }
      
      // Clean up color_images - only keep references to valid images
      if (data.color_images && Object.keys(data.color_images).length > 0) {
        for (const [color, imgs] of Object.entries(data.color_images)) {
          const validColorImgs = imgs.filter(img => validImages.includes(img));
          if (validColorImgs.length > 0) {
            validColorImages[color] = validColorImgs;
          }
        }
      }
      
      // If images were cleaned, show a message
      const removedCount = (data.images?.length || 0) - validImages.length;
      if (removedCount > 0) {
        console.log(`Cleaned up ${removedCount} broken image(s)`);
      }
      
      setFormData({
        ...data,
        price: data.price?.toString() || '',
        original_price: data.original_price?.toString() || '',
        images: validImages.length ? validImages : [''],
        color_images: validColorImages,
      });
    } catch (error) {
      console.error('Error loading product:', error);
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        images: formData.images.filter((img) => img.trim() !== ''),
        color_images: formData.color_images || {},
      };

      if (isNew) {
        await createProduct(productData);
      } else {
        await updateProduct(productId, productData);
      }

      navigate('/admin');
    } catch (error) {
      console.error('Error saving product:', error);
      alert(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const updateTranslation = (lang, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value,
        },
      },
    }));
  };

  const addVariant = () => {
    setFormData((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        { size: 'M', color: 'Noir', color_hex: '#000000', stock: 10 },
      ],
    }));
  };

  const updateVariant = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === index ? { ...v, [field]: field === 'stock' ? parseInt(value) || 0 : value } : v
      ),
    }));
  };

  const removeVariant = (index) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  const addImage = () => {
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ''],
    }));
  };

  const updateImage = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.map((img, i) => (i === index ? value : img)),
    }));
  };

  // Set an image as primary (move to first position)
  const setAsPrimaryImage = (index) => {
    setFormData((prev) => {
      const images = [...prev.images.filter(img => img.trim() !== '')];
      if (index <= 0 || index >= images.length) return prev;
      
      // Move the selected image to the first position
      const [selectedImage] = images.splice(index, 1);
      images.unshift(selectedImage);
      
      return {
        ...prev,
        images: images,
      };
    });
  };

  // Helper to extract Cloudinary public_id from URL
  const getCloudinaryPublicId = (url) => {
    if (!url || !url.includes('res.cloudinary.com')) return null;
    try {
      // URL format: https://res.cloudinary.com/{cloud}/image/upload/v{version}/{folder}/{filename}.{ext}
      // or: https://res.cloudinary.com/{cloud}/image/upload/{folder}/{filename}.{ext}
      const uploadIndex = url.indexOf('/upload/');
      if (uploadIndex === -1) return null;
      
      let path = url.substring(uploadIndex + 8); // after '/upload/'
      
      // Remove version if present (v1234567890/)
      if (path.match(/^v\d+\//)) {
        path = path.replace(/^v\d+\//, '');
      }
      
      // Remove file extension
      const lastDot = path.lastIndexOf('.');
      if (lastDot !== -1) {
        path = path.substring(0, lastDot);
      }
      
      return path; // e.g., "products/abc123"
    } catch (e) {
      console.error('Error extracting Cloudinary public_id:', e);
      return null;
    }
  };

  const removeImage = async (index) => {
    const imageToRemove = formData.images[index];
    
    // If it's a Cloudinary image, delete from Cloudinary first
    const publicId = getCloudinaryPublicId(imageToRemove);
    if (publicId && cloudinaryEnabled) {
      try {
        const token = localStorage.getItem('ebb_token');
        const response = await fetch(`${BACKEND_URL}/api/cloudinary/${encodeURIComponent(publicId)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          console.warn('Failed to delete from Cloudinary, but continuing with local removal');
        }
      } catch (e) {
        console.error('Error deleting from Cloudinary:', e);
        // Continue with local removal anyway
      }
    }
    
    // Update local state
    setFormData((prev) => {
      const newImages = prev.images.filter((_, i) => i !== index);
      
      // Also clean up color_images - remove this image URL from all color associations
      const newColorImages = { ...prev.color_images };
      Object.keys(newColorImages).forEach(color => {
        newColorImages[color] = newColorImages[color].filter(img => img !== imageToRemove);
        // Remove the color key if no images left
        if (newColorImages[color].length === 0) {
          delete newColorImages[color];
        }
      });
      
      return {
        ...prev,
        images: newImages,
        color_images: newColorImages,
      };
    });
  };

  // Check if Cloudinary is configured
  const [cloudinaryEnabled, setCloudinaryEnabled] = useState(false);
  
  useEffect(() => {
    const checkCloudinary = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cloudinary/status`);
        const data = await response.json();
        setCloudinaryEnabled(data.configured);
      } catch (e) {
        setCloudinaryEnabled(false);
      }
    };
    checkCloudinary();
  }, []);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    try {
      for (const file of files) {
        let imageUrl;
        
        if (cloudinaryEnabled) {
          // Upload to Cloudinary (cloud storage)
          const token = localStorage.getItem('ebb_token');
          
          // Get signature
          const sigResponse = await fetch(`${BACKEND_URL}/api/cloudinary/signature?folder=products/`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const sigData = await sigResponse.json();
          
          // Upload to Cloudinary
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          formDataUpload.append('api_key', sigData.api_key);
          formDataUpload.append('timestamp', sigData.timestamp);
          formDataUpload.append('signature', sigData.signature);
          formDataUpload.append('folder', sigData.folder);
          
          const uploadResponse = await fetch(
            `https://api.cloudinary.com/v1_1/${sigData.cloud_name}/image/upload`,
            { method: 'POST', body: formDataUpload }
          );
          
          const result = await uploadResponse.json();
          imageUrl = result.secure_url;
        } else {
          // Fallback to local upload
          const result = await uploadImage(file);
          imageUrl = `${BACKEND_URL}${result.url}`;
        }
        
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images.filter(img => img.trim() !== ''), imageUrl],
        }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(language === 'fr' ? 'Erreur lors de l\'upload' : 'Fout bij uploaden');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Helper function to get displayable image URL
  const getImageUrl = (img) => {
    if (!img) return '';
    // If it's already a full URL, return it
    if (img.startsWith('http')) return img;
    // If it's a relative path, prepend backend URL
    return `${BACKEND_URL}${img}`;
  };

  // Get unique colors from variants
  const getUniqueColors = () => {
    const colors = formData.variants.map(v => ({ color: v.color, hex: v.color_hex }));
    return colors.filter((c, index, self) => 
      index === self.findIndex(t => t.color === c.color)
    );
  };

  // Toggle image assignment to a color
  const toggleImageForColor = (imageUrl, color) => {
    setFormData(prev => {
      const colorImages = { ...prev.color_images };
      if (!colorImages[color]) {
        colorImages[color] = [];
      }
      
      const imageIndex = colorImages[color].indexOf(imageUrl);
      if (imageIndex > -1) {
        // Remove image from color
        colorImages[color] = colorImages[color].filter(img => img !== imageUrl);
        if (colorImages[color].length === 0) {
          delete colorImages[color];
        }
      } else {
        // Add image to color
        colorImages[color] = [...colorImages[color], imageUrl];
      }
      
      return { ...prev, color_images: colorImages };
    });
  };

  // Check if image is assigned to a color
  const isImageAssignedToColor = (imageUrl, color) => {
    return formData.color_images[color]?.includes(imageUrl) || false;
  };

  // Get images assigned to a specific color (or all if no assignment)
  const getImagesForColor = (color) => {
    if (formData.color_images[color]?.length > 0) {
      return formData.color_images[color];
    }
    return formData.images.filter(img => img.trim() !== '');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-700 border-t-transparent"></div>
      </div>
    );
  }

  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  return (
    <div className="min-h-screen bg-gray-100" data-testid="product-edit-page">
      {/* Header */}
      <div className="bg-amber-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center text-amber-200 hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t.common.back}
          </button>
          <h1 className="text-3xl font-bold text-white">
            {isNew ? t.admin.addProduct : t.admin.editProduct}
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Informations générales' : 'Algemene informatie'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'fr' ? 'Catégorie' : 'Categorie'} *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.products.price} (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.products.originalPrice} (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.original_price}
                  onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  placeholder={language === 'fr' ? 'Laisser vide si pas de promo' : 'Leeg laten als geen promotie'}
                />
              </div>
              <div className="flex items-center gap-6 pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-4 h-4 text-amber-600 focus:ring-amber-500 rounded"
                  />
                  <span className="text-sm text-gray-700">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.new_arrival}
                    onChange={(e) => setFormData({ ...formData, new_arrival: e.target.checked })}
                    className="w-4 h-4 text-amber-600 focus:ring-amber-500 rounded"
                  />
                  <span className="text-sm text-gray-700">{t.products.new}</span>
                </label>
              </div>
            </div>
          </div>

          {/* French Translations */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              🇫🇷 {language === 'fr' ? 'Textes français' : 'Franse teksten'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'fr' ? 'Nom du produit' : 'Productnaam'} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.translations.fr.name}
                  onChange={(e) => updateTranslation('fr', 'name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  required
                  rows={3}
                  value={formData.translations.fr.description}
                  onChange={(e) => updateTranslation('fr', 'description', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.products.material}
                  </label>
                  <input
                    type="text"
                    value={formData.translations.fr.material}
                    onChange={(e) => updateTranslation('fr', 'material', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.products.care}
                  </label>
                  <input
                    type="text"
                    value={formData.translations.fr.care_instructions}
                    onChange={(e) => updateTranslation('fr', 'care_instructions', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Dutch Translations */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              🇳🇱 {language === 'fr' ? 'Textes néerlandais' : 'Nederlandse teksten'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'fr' ? 'Nom du produit' : 'Productnaam'} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.translations.nl.name}
                  onChange={(e) => updateTranslation('nl', 'name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  required
                  rows={3}
                  value={formData.translations.nl.description}
                  onChange={(e) => updateTranslation('nl', 'description', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.products.material}
                  </label>
                  <input
                    type="text"
                    value={formData.translations.nl.material}
                    onChange={(e) => updateTranslation('nl', 'material', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.products.care}
                  </label>
                  <input
                    type="text"
                    value={formData.translations.nl.care_instructions}
                    onChange={(e) => updateTranslation('nl', 'care_instructions', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900">Images</h2>
              <div className="flex gap-2">
                {/* Reset button - clears all images and associations */}
                {formData.images.filter(img => img.trim() !== '').length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(language === 'fr' 
                        ? 'Supprimer toutes les images et associations ?' 
                        : 'Alle afbeeldingen en koppelingen verwijderen?')) {
                        setFormData(prev => ({
                          ...prev,
                          images: [''],
                          color_images: {}
                        }));
                      }
                    }}
                    className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm"
                    title={language === 'fr' ? 'Réinitialiser toutes les images' : 'Alle afbeeldingen resetten'}
                  >
                    <Trash2 className="w-4 h-4" />
                    {language === 'fr' ? 'Tout effacer' : 'Alles wissen'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-1 bg-amber-700 hover:bg-amber-800 disabled:bg-gray-400 text-white px-3 py-1.5 rounded-lg text-sm"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  {language === 'fr' ? 'Uploader' : 'Uploaden'}
                </button>
                <button
                  type="button"
                  onClick={addImage}
                  className="flex items-center gap-1 text-amber-700 hover:text-amber-800"
                >
                  <Plus className="w-4 h-4" />
                  {language === 'fr' ? 'URL' : 'URL'}
                </button>
              </div>
            </div>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
            
            {/* Image previews */}
            {formData.images.filter(img => img.trim() !== '').length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {formData.images.filter(img => img.trim() !== '').map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={getImageUrl(img)}
                      alt={`Product ${index + 1}`}
                      className={`w-full h-32 object-cover rounded-lg border-2 ${
                        index === 0 ? 'border-amber-500' : 'border-gray-200'
                      }`}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/150?text=Image';
                      }}
                    />
                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={() => removeImage(formData.images.indexOf(img))}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      title={language === 'fr' ? 'Supprimer' : 'Verwijderen'}
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {/* Set as primary button - only show for non-primary images */}
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => setAsPrimaryImage(index)}
                        className="absolute top-1 left-1 bg-gray-700 hover:bg-amber-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title={language === 'fr' ? 'Définir comme image principale' : 'Instellen als hoofdafbeelding'}
                      >
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    {/* Primary badge */}
                    {index === 0 && (
                      <span className="absolute bottom-1 left-1 bg-amber-700 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        {language === 'fr' ? 'Principal' : 'Hoofd'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* URL inputs for images */}
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-2">
                {language === 'fr' 
                  ? 'Vous pouvez aussi ajouter des images via URL :' 
                  : 'U kunt ook afbeeldingen toevoegen via URL:'}
              </p>
              {formData.images.map((img, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="url"
                    value={img}
                    onChange={(e) => updateImage(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    placeholder="https://..."
                  />
                  {formData.images.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="p-2 text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Color-Image Associations */}
          {getUniqueColors().length > 0 && formData.images.filter(img => img.trim() !== '').length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                {language === 'fr' ? 'Association Images ↔ Couleurs' : 'Afbeelding ↔ Kleur Koppeling'}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                {language === 'fr' 
                  ? 'Cliquez sur une couleur puis sélectionnez les images à associer. Les images non assignées seront affichées pour toutes les couleurs.'
                  : 'Klik op een kleur en selecteer de afbeeldingen om te koppelen. Niet-toegewezen afbeeldingen worden voor alle kleuren weergegeven.'}
              </p>

              {/* Color selector */}
              <div className="flex flex-wrap gap-2 mb-4">
                {getUniqueColors().map((colorObj, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setSelectedColorForImages(
                      selectedColorForImages === colorObj.color ? null : colorObj.color
                    )}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      selectedColorForImages === colorObj.color
                        ? 'border-amber-600 bg-amber-50 ring-2 ring-amber-300'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <span
                      className="w-5 h-5 rounded-full border border-gray-300"
                      style={{ backgroundColor: colorObj.hex }}
                    />
                    <span className="font-medium">{colorObj.color}</span>
                    {formData.color_images[colorObj.color]?.length > 0 && (
                      <span className="bg-amber-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                        {formData.color_images[colorObj.color].length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Image selection for selected color */}
              {selectedColorForImages && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    {language === 'fr' 
                      ? `Sélectionnez les images pour "${selectedColorForImages}" :`
                      : `Selecteer afbeeldingen voor "${selectedColorForImages}":`}
                  </p>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {formData.images.filter(img => img.trim() !== '').map((img, index) => {
                      const isAssigned = isImageAssignedToColor(img, selectedColorForImages);
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => toggleImageForColor(img, selectedColorForImages)}
                          className={`relative rounded-lg overflow-hidden border-3 transition-all ${
                            isAssigned
                              ? 'border-amber-600 ring-2 ring-amber-400 scale-105'
                              : 'border-gray-200 hover:border-gray-400 opacity-60 hover:opacity-100'
                          }`}
                        >
                          <img
                            src={getImageUrl(img)}
                            alt={`Image ${index + 1}`}
                            className="w-full h-20 object-cover"
                          />
                          {isAssigned && (
                            <div className="absolute inset-0 bg-amber-600/20 flex items-center justify-center">
                              <div className="bg-amber-600 text-white rounded-full p-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Summary of assignments */}
              {Object.keys(formData.color_images).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs font-medium text-gray-500 mb-2">
                    {language === 'fr' ? 'Résumé des associations :' : 'Overzicht koppelingen:'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(formData.color_images).map(([color, imgs]) => (
                      <span key={color} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                        <span
                          className="w-3 h-3 rounded-full border"
                          style={{ 
                            backgroundColor: getUniqueColors().find(c => c.color === color)?.hex || '#ccc'
                          }}
                        />
                        {color}: {imgs.length} image{imgs.length > 1 ? 's' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Variants */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900">
                {language === 'fr' ? 'Variantes (Tailles/Couleurs)' : 'Varianten (Maten/Kleuren)'}
              </h2>
              <button
                type="button"
                onClick={addVariant}
                className="flex items-center gap-1 text-amber-700 hover:text-amber-800"
              >
                <Plus className="w-4 h-4" />
                {language === 'fr' ? 'Ajouter' : 'Toevoegen'}
              </button>
            </div>
            <div className="space-y-4">
              {formData.variants.map((variant, index) => (
                <div
                  key={index}
                  className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {t.products.size}
                    </label>
                    <select
                      value={variant.size}
                      onChange={(e) => updateVariant(index, 'size', e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-amber-500"
                    >
                      {sizes.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {t.products.color}
                    </label>
                    <input
                      type="text"
                      value={variant.color}
                      onChange={(e) => updateVariant(index, 'color', e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {language === 'fr' ? 'Code couleur' : 'Kleurcode'}
                    </label>
                    <input
                      type="color"
                      value={variant.color_hex}
                      onChange={(e) => updateVariant(index, 'color_hex', e.target.value)}
                      className="w-full h-8 border border-gray-300 rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Stock</label>
                    <input
                      type="number"
                      min="0"
                      value={variant.stock}
                      onChange={(e) => updateVariant(index, 'stock', e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div className="flex items-end">
                    {formData.variants.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVariant(index)}
                        className="p-2 text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.admin.cancel}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-amber-700 hover:bg-amber-800 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t.common.loading}
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {t.admin.save}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductEditPage;
